import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DestinationSelect, DestinationCity } from './DestinationSelect';
import { DateRangePicker, DateRangeData } from './DateRangePicker';
import { TravelersDropdown, TravelersData } from './TravelersDropdown';
import { StyleChips } from './StyleChips';
import CaptchaModal from '../common/CaptchaModal';
import { useUserLocation } from '../../hooks/useUserLocation';
import { logger } from '../../utils/logger';
import './TripBuilderForm.css';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface TripBuilderFormProps {
    onGenerating?: () => void;
    buttonText?: string;
}

type GenerateAsyncResponse = {
    tripId: string;
    slug: string;
    question: string;
    questionType: string;
    editToken: string;
    destination?: {
        name: string;
        country: string;
        lat: number;
        lng: number;
    };
};

type ActivePanel = 'origin' | 'destination' | 'date' | 'travelers' | null;

function toISODateOnly(value: any): string | null {
    if (!value) return null;
    if (typeof value === 'string') {
        return value.length >= 10 ? value.slice(0, 10) : value;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
    }
    return null;
}

export function TripBuilderForm({ onGenerating, buttonText }: TripBuilderFormProps) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [destinations, setDestinations] = useState<DestinationCity[]>([]);
    const [dateRange, setDateRange] = useState<DateRangeData>({
        start: null,
        end: null,
        flexible: true,
    });
    const [travelers, setTravelers] = useState<TravelersData>({ adults: 2, children: 0, infants: 0 });
    const [styles, setStyles] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [activePanel, setActivePanel] = useState<ActivePanel>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [mobileExpanded, setMobileExpanded] = useState(false);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [pendingPayload, setPendingPayload] = useState<any>(null);
    
    // User origin from IP geolocation (stored as DestinationCity for consistency with DestinationSelect)
    const { location: ipLocation, loading: locationLoading } = useUserLocation();
    const [originCity, setOriginCity] = useState<DestinationCity | null>(null);
    // Store coordinates separately (used for transport calculation)
    const [originCoordinates, setOriginCoordinates] = useState<{ lat: number; lon: number } | null>(null);
    
    const containerRef = useRef<HTMLDivElement>(null);

    // Check for mobile
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Build suggested origin from IP geolocation (shown as suggestion, not pre-filled)
    const suggestedOrigin = useMemo(() => {
        if (ipLocation && ipLocation.city) {
            return {
                id: 0,
                name: ipLocation.city,
                country: ipLocation.country || '',
                countryCode: ipLocation.countryCode || '',
            };
        }
        return null;
    }, [ipLocation]);

    // Close panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setActivePanel(null);
                if (isMobile) setMobileExpanded(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobile]);

    const isDateValid = useMemo(() => {
        if (dateRange.flexible) return true;
        const s = toISODateOnly(dateRange.start);
        const e = toISODateOnly(dateRange.end);
        return Boolean(s && e);
    }, [dateRange]);

    const canSubmit = destinations.length > 0 && isDateValid && travelers.adults > 0;

    const submitWithPayload = async (payload: any, captchaToken?: string) => {
        const finalPayload = captchaToken ? { ...payload, captchaToken } : payload;
        
        const res = await fetch(`${API_BASE}/api/trip/generate-async`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(finalPayload),
        });

        const responseData = await res.json().catch(() => ({}));

        if (!res.ok) {
            // Check if captcha is required
            if (responseData.requiresCaptcha) {
                setPendingPayload(payload);
                setShowCaptcha(true);
                setIsSubmitting(false);
                return null;
            }
            throw new Error(responseData.error || 'Generate failed');
        }

        return responseData as GenerateAsyncResponse;
    };

    const handleSubmit = async () => {
        if (!canSubmit || isSubmitting) return;

        setIsSubmitting(true);
        setErrorMsg(null);
        onGenerating?.();

        try {
            const payload = {
                destinations: destinations.map((d) => d.id),
                dateRange: dateRange.flexible
                    ? { flexible: true }
                    : {
                        start: toISODateOnly(dateRange.start),
                        end: toISODateOnly(dateRange.end),
                    },
                travelers: {
                    adults: travelers.adults,
                    children: travelers.children,
                    infants: travelers.infants,
                },
                styles,
                language: i18n.language?.startsWith('ro') ? 'ro' : 'en',
                currency: 'EUR',
                // Include user origin for smart transport suggestions
                userOrigin: originCity ? {
                    city: originCity.name,
                    country: originCity.country || '',
                    countryCode: '',
                    // Include coordinates for accurate distance calculation
                    lat: originCoordinates?.lat,
                    lon: originCoordinates?.lon,
                } : undefined,
            };

            const data = await submitWithPayload(payload);
            
            // If captcha was required, submitWithPayload returns null and shows modal
            if (!data) return;

            if (!data?.tripId || !data?.slug) {
                throw new Error('Invalid response from server');
            }

            if (data.question) {
                sessionStorage.setItem(`trip_${data.tripId}_question`, data.question);
            }
            
            if (data.questionType) {
                sessionStorage.setItem(`trip_${data.tripId}_questionType`, data.questionType);
            }
            
            if (data.editToken) {
                sessionStorage.setItem(`trip_${data.tripId}_editToken`, data.editToken);
            }

            if (data.destination) {
                sessionStorage.setItem(`trip_${data.tripId}_destination`, JSON.stringify(data.destination));
            }

            // Store trip summary for GeneratingView
            const tripSummary = {
                destinations: destinations.map(d => d.name),
                travelers: travelers.adults + travelers.children,
                dateRange: dateRange.flexible 
                    ? { flexible: true }
                    : { start: dateRange.start, end: dateRange.end },
            };
            sessionStorage.setItem(`trip_${data.tripId}_summary`, JSON.stringify(tripSummary));

            // Navigate with language prefix to preserve locale
            const langPrefix = i18n.language?.startsWith('ro') ? '/ro' : '';
            navigate({ to: `${langPrefix}/trip/${data.slug}` } as any);
        } catch (err: any) {
            logger.error('Trip generation failed:', err);
            setErrorMsg(err?.message || t('tripBuilder.error', 'Something went wrong. Please try again.'));
            setIsSubmitting(false);
        }
    };

    const handleCaptchaVerify = async (token: string) => {
        setShowCaptcha(false);
        
        if (!pendingPayload) return;
        
        setIsSubmitting(true);
        setErrorMsg(null);
        onGenerating?.();

        try {
            const data = await submitWithPayload(pendingPayload, token);
            
            if (!data) {
                // Should not happen after captcha, but handle anyway
                setErrorMsg(t('tripBuilder.captcha_failed', 'Captcha verification failed. Please try again.'));
                setIsSubmitting(false);
                return;
            }

            if (!data?.tripId || !data?.slug) {
                throw new Error('Invalid response from server');
            }

            if (data.question) {
                sessionStorage.setItem(`trip_${data.tripId}_question`, data.question);
            }
            
            if (data.questionType) {
                sessionStorage.setItem(`trip_${data.tripId}_questionType`, data.questionType);
            }
            
            if (data.editToken) {
                sessionStorage.setItem(`trip_${data.tripId}_editToken`, data.editToken);
            }

            if (data.destination) {
                sessionStorage.setItem(`trip_${data.tripId}_destination`, JSON.stringify(data.destination));
            }

            // Store trip summary for GeneratingView
            const tripSummary = {
                destinations: destinations.map(d => d.name),
                travelers: travelers.adults + travelers.children,
                dateRange: dateRange.flexible 
                    ? { flexible: true }
                    : { start: dateRange.start, end: dateRange.end },
            };
            sessionStorage.setItem(`trip_${data.tripId}_summary`, JSON.stringify(tripSummary));

            setPendingPayload(null);
            // Navigate with language prefix to preserve locale
            const langPrefix = i18n.language?.startsWith('ro') ? '/ro' : '';
            navigate({ to: `${langPrefix}/trip/${data.slug}` } as any);
        } catch (err: any) {
            logger.error('Trip generation failed after captcha:', err);
            setErrorMsg(err?.message || t('tripBuilder.error', 'Something went wrong. Please try again.'));
            setIsSubmitting(false);
            setPendingPayload(null);
        }
    };

    const handleCaptchaClose = () => {
        setShowCaptcha(false);
        setPendingPayload(null);
    };

    const handlePanelToggle = useCallback((panel: ActivePanel) => {
        if (isMobile && !mobileExpanded) {
            setMobileExpanded(true);
            setActivePanel(panel);
        } else {
            setActivePanel(prev => prev === panel ? null : panel);
        }
    }, [isMobile, mobileExpanded]);

    // Track previous values for auto-advance logic
    const prevDestinationsCount = useRef(destinations.length);
    const prevDateComplete = useRef(false);

    const handleDestinationChange = useCallback((cities: DestinationCity[]) => {
        const capped = cities.slice(0, 4); // max 4 destinations
        if (capped.length > prevDestinationsCount.current) {
            setTimeout(() => setActivePanel('date'), 150);
        }
        prevDestinationsCount.current = capped.length;
        setDestinations(capped);
    }, []);

    const handleDateChange = useCallback((data: DateRangeData) => {
        // If dates are now complete (both start and end selected), auto-advance to travelers
        const isNowComplete = Boolean(data.start && data.end && !data.flexible);
        if (!prevDateComplete.current && isNowComplete) {
            setTimeout(() => setActivePanel('travelers'), 150);
        }
        prevDateComplete.current = isNowComplete;
        setDateRange(data);
    }, []);

    const handleTravelersChange = useCallback((data: TravelersData) => {
        setTravelers(data);
    }, []);

    // Format display text (single destination: "Country, City" when country is set)
    const getDestinationDisplay = () => {
        if (destinations.length === 0) return t('tripBuilder.destination_placeholder', 'Search by city or landmark');
        if (destinations.length === 1) {
            const c = destinations[0];
            return c.country ? `${c.country}, ${c.name}` : c.name;
        }
        return `${destinations.length} ${t('tripBuilder.destinations_count', 'destinations')}`;
    };

    const getDateDisplay = () => {
        if (dateRange.flexible) return t('tripBuilder.date_add', 'Add dates');
        if (dateRange.start && dateRange.end) {
            const formatDate = (dateStr: string) => {
                const d = new Date(dateStr);
                return d.toLocaleDateString(i18n.language === 'ro' ? 'ro-RO' : 'en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                });
            };
            return `${formatDate(dateRange.start)} – ${formatDate(dateRange.end)}`;
        }
        return t('tripBuilder.date_add', 'Add dates');
    };

    const getTravelersDisplay = () => {
        const total = travelers.adults + travelers.children;
        const parts: string[] = [];
        
        if (total === 1) {
            parts.push(`1 ${t('tripBuilder.guest_single', 'guest')}`);
        } else {
            parts.push(`${total} ${t('tripBuilder.guests_plural', 'guests')}`);
        }
        
        if (travelers.infants > 0) {
            parts.push(`${travelers.infants} ${travelers.infants === 1 ? t('tripBuilder.infant_single', 'infant') : t('tripBuilder.infants_plural', 'infants')}`);
        }
        
        return parts.join(', ') || t('tripBuilder.guests_add', 'Add guests');
    };

    const getOriginDisplay = () => {
        if (locationLoading) return t('tripBuilder.detecting_location', 'Detecting...');
        if (!originCity) return t('tripBuilder.from_placeholder', 'Your city');
        return originCity.country ? `${originCity.country}, ${originCity.name}` : originCity.name;
    };

    // Origin change handler (same pattern as destination)
    const handleOriginChange = useCallback((cities: DestinationCity[]) => {
        // Only allow single origin
        if (cities.length > 0) {
            const selectedCity = cities[0];
            setOriginCity(selectedCity);
            
            // Check if this is the suggested origin from IP geolocation
            // If so, we have coordinates from ipLocation
            if (ipLocation && 
                selectedCity.name === ipLocation.city && 
                ipLocation.lat !== null && 
                ipLocation.lon !== null) {
                setOriginCoordinates({ lat: ipLocation.lat, lon: ipLocation.lon });
            } else {
                // User searched for a different city - no coordinates available
                // Backend will try to look it up by name
                setOriginCoordinates(null);
            }
            
            // Auto-advance to destination after selecting origin
            setTimeout(() => setActivePanel('destination'), 150);
        } else {
            setOriginCity(null);
            setOriginCoordinates(null);
        }
    }, [ipLocation]);

    // Mobile collapsed view
    if (isMobile && !mobileExpanded) {
        return (
            <div className="trip-builder-wrapper" ref={containerRef}>
                <button 
                    className="search-bar-mobile-trigger"
                    onClick={() => setMobileExpanded(true)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <span>{t('tripBuilder.start_search', 'Start your search')}</span>
                </button>
                
                <div className="styles-container">
                    <StyleChips selected={styles} onChange={setStyles} />
                </div>
            </div>
        );
    }

    // Mobile expanded view
    if (isMobile && mobileExpanded) {
        return (
            <div className="trip-builder-wrapper mobile-expanded" ref={containerRef}>
                <div className="mobile-modal-overlay">
                    <div className="mobile-search-modal">
                        {/* Origin Section (From) */}
                        <div 
                            className={`mobile-section ${activePanel === 'origin' ? 'active' : ''}`}
                            onClick={() => handlePanelToggle('origin')}
                        >
                            <div className="mobile-section-header">
                                <span className="mobile-section-title">{t('tripBuilder.from_label', 'From')}</span>
                                {activePanel !== 'origin' && (
                                    <span className="mobile-section-value">{getOriginDisplay()}</span>
                                )}
                            </div>
                            {activePanel === 'origin' && (
                                <div className="mobile-section-content" onClick={e => e.stopPropagation()}>
                                    <DestinationSelect 
                                        selected={originCity ? [originCity] : []} 
                                        onChange={handleOriginChange}
                                        isExpanded={true}
                                        maxDestinations={1}
                                        placeholder={t('tripBuilder.from_search_placeholder', 'Enter your city')}
                                        suggestedCity={suggestedOrigin}
                                        suggestedLabel={t('tripBuilder.suggested_departure', 'Suggested departure')}
                                        hideSuggestedDestinations={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Destination Section */}
                        <div 
                            className={`mobile-section ${activePanel === 'destination' ? 'active' : ''}`}
                            onClick={() => handlePanelToggle('destination')}
                        >
                            <div className="mobile-section-header">
                                <span className="mobile-section-title">{t('tripBuilder.where_label', 'Where?')}</span>
                                {activePanel !== 'destination' && (
                                    <span className="mobile-section-value">{getDestinationDisplay()}</span>
                                )}
                            </div>
                            {activePanel === 'destination' && (
                                <div className="mobile-section-content" onClick={e => e.stopPropagation()}>
                                    <DestinationSelect 
                                        selected={destinations} 
                                        onChange={handleDestinationChange}
                                        isExpanded={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Date Section */}
                        <div 
                            className={`mobile-section ${activePanel === 'date' ? 'active' : ''}`}
                            onClick={() => handlePanelToggle('date')}
                        >
                            <div className="mobile-section-header">
                                <span className="mobile-section-title">{t('tripBuilder.when_label', 'When')}</span>
                                <span className="mobile-section-value">{getDateDisplay()}</span>
                            </div>
                            {activePanel === 'date' && (
                                <div className="mobile-section-content" onClick={e => e.stopPropagation()}>
                                    <DateRangePicker 
                                        value={dateRange} 
                                        onChange={handleDateChange}
                                        isExpanded={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Travelers Section */}
                        <div 
                            className={`mobile-section ${activePanel === 'travelers' ? 'active' : ''}`}
                            onClick={() => handlePanelToggle('travelers')}
                        >
                            <div className="mobile-section-header">
                                <span className="mobile-section-title">{t('tripBuilder.who_label', 'Who')}</span>
                                <span className="mobile-section-value">{getTravelersDisplay()}</span>
                            </div>
                            {activePanel === 'travelers' && (
                                <div className="mobile-section-content" onClick={e => e.stopPropagation()}>
                                    <TravelersDropdown 
                                        value={travelers} 
                                        onChange={handleTravelersChange}
                                        isExpanded={true}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Footer Actions */}
                        <div className="mobile-modal-footer">
                            <button 
                                className="mobile-clear-btn"
                                onClick={() => {
                                    setDestinations([]);
                                    setDateRange({ start: null, end: null, flexible: true });
                                    setTravelers({ adults: 2, children: 0, infants: 0 });
                                }}
                            >
                                {t('tripBuilder.clear_all', 'Clear all')}
                            </button>
                            <button 
                                className="mobile-search-btn"
                                onClick={handleSubmit}
                                disabled={!canSubmit || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <div className="spinner" />
                                ) : (
                                    <>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <circle cx="11" cy="11" r="8"></circle>
                                            <path d="m21 21-4.35-4.35"></path>
                                        </svg>
                                        {buttonText || t('tripBuilder.search', 'Search')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {errorMsg && (
                    <div className="trip-builder-error" role="alert">
                        {errorMsg}
                    </div>
                )}

                {/* Captcha Modal */}
                <CaptchaModal
                    isOpen={showCaptcha}
                    onVerify={handleCaptchaVerify}
                    onClose={handleCaptchaClose}
                    title={t('tripBuilder.captcha_title', 'Security verification')}
                    description={t('tripBuilder.captcha_description', 'Please complete the verification to continue.')}
                />
            </div>
        );
    }

    // Desktop view
    return (
        <div className="trip-builder-wrapper" ref={containerRef}>
            <div className="trip-builder-container">
                <div className={`search-bar ${activePanel ? 'has-active-panel' : ''}`}>
                    {/* From Section (Origin) */}
                    <div 
                        className={`search-section origin-section ${activePanel === 'origin' ? 'active' : ''}`}
                        onClick={() => handlePanelToggle('origin')}
                    >
                        <div className="section-content">
                            <span className="section-label">{t('tripBuilder.from_label', 'From')}</span>
                            <span className={`section-value ${!originCity ? 'placeholder' : ''}`}>
                                {getOriginDisplay()}
                            </span>
                        </div>
                        
                        {activePanel === 'origin' && (
                            <div className="dropdown-panel origin-dropdown" onClick={e => e.stopPropagation()}>
                                <DestinationSelect 
                                    selected={originCity ? [originCity] : []} 
                                    onChange={handleOriginChange}
                                    isExpanded={true}
                                    maxDestinations={1}
                                    placeholder={t('tripBuilder.from_search_placeholder', 'Enter your city')}
                                    suggestedCity={suggestedOrigin}
                                    suggestedLabel={t('tripBuilder.suggested_departure', 'Suggested departure')}
                                    hideSuggestedDestinations={true}
                                />
                            </div>
                        )}
                    </div>

                    <div className="divider" />

                    {/* Where Section */}
                    <div 
                        className={`search-section destination-section ${activePanel === 'destination' ? 'active' : ''}`}
                        onClick={() => handlePanelToggle('destination')}
                    >
                        <div className="section-content">
                            <span className="section-label">{t('tripBuilder.where_label', 'Where')}</span>
                            <span className={`section-value ${destinations.length === 0 ? 'placeholder' : ''}`}>
                                {getDestinationDisplay()}
                            </span>
                        </div>
                        
                        {activePanel === 'destination' && (
                            <div className="dropdown-panel destination-dropdown" onClick={e => e.stopPropagation()}>
                                <DestinationSelect 
                                    selected={destinations} 
                                    onChange={handleDestinationChange}
                                    isExpanded={true}
                                />
                            </div>
                        )}
                    </div>

                    <div className="divider" />

                    {/* When Section */}
                    <div 
                        className={`search-section period-section ${activePanel === 'date' ? 'active' : ''}`}
                        onClick={() => handlePanelToggle('date')}
                    >
                        <div className="section-content">
                            <span className="section-label">{t('tripBuilder.when_label', 'When')}</span>
                            <span className={`section-value ${dateRange.flexible ? 'placeholder' : ''}`}>
                                {getDateDisplay()}
                            </span>
                        </div>
                        
                        {activePanel === 'date' && (
                            <div className="dropdown-panel date-dropdown" onClick={e => e.stopPropagation()}>
                                <DateRangePicker 
                                    value={dateRange} 
                                    onChange={handleDateChange}
                                    isExpanded={true}
                                />
                            </div>
                        )}
                    </div>

                    <div className="divider" />

                    {/* Who Section */}
                    <div 
                        className={`search-section travelers-section ${activePanel === 'travelers' ? 'active' : ''}`}
                        onClick={() => handlePanelToggle('travelers')}
                    >
                        <div className="section-content">
                            <span className="section-label">{t('tripBuilder.who_label', 'Who')}</span>
                            <span className={`section-value ${travelers.adults === 0 && travelers.children === 0 ? 'placeholder' : ''}`}>
                                {getTravelersDisplay()}
                            </span>
                        </div>
                        
                        {activePanel === 'travelers' && (
                            <div className="dropdown-panel travelers-dropdown" onClick={e => e.stopPropagation()}>
                                <TravelersDropdown 
                                    value={travelers} 
                                    onChange={handleTravelersChange}
                                    isExpanded={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Search Button */}
                    <div className="search-action">
                        <button
                            type="button"
                            className="search-button"
                            disabled={!canSubmit || isSubmitting}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? (
                                <div className="spinner" />
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <path d="m21 21-4.35-4.35"></path>
                                    </svg>
                                    <span className="search-label">{buttonText || t('tripBuilder.search', 'Search')}</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {errorMsg && (
                <div className="trip-builder-error" role="alert">
                    {errorMsg}
                </div>
            )}

            <div className="styles-container">
                <StyleChips selected={styles} onChange={setStyles} />
            </div>

            {/* Captcha Modal */}
            <CaptchaModal
                isOpen={showCaptcha}
                onVerify={handleCaptchaVerify}
                onClose={handleCaptchaClose}
                title={t('tripBuilder.captcha_title', 'Security verification')}
                description={t('tripBuilder.captcha_description', 'Please complete the verification to continue.')}
            />
        </div>
    );
}
